import { useMemo, useState } from "react";
import { Plus, Minus, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const DEFAULT_PEOPLE = [
  { name: "You", emoji: "🧑" },
  { name: "Aryan", emoji: "🧔" },
  { name: "Maya", emoji: "👩" },
];

export const DemoCalculator = () => {
  const [amount, setAmount] = useState(2400);
  const [people, setPeople] = useState(DEFAULT_PEOPLE);

  const split = useMemo(() => (amount / people.length).toFixed(2), [amount, people.length]);

  const add = () => {
    if (people.length >= 6) return;
    const names = ["Raj", "Zoe", "Neo", "Mia", "Leo"];
    setPeople([...people, { name: names[people.length - 3] || "Friend", emoji: "🙂" }]);
  };
  const remove = () => {
    if (people.length <= 2) return;
    setPeople(people.slice(0, -1));
  };

  return (
    <div className="relative rounded-2xl border border-white/10 glass-strong p-6 sm:p-7 shadow-[0_20px_80px_-20px_rgba(0,255,102,0.15)]" data-testid="demo-calculator">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/40">LIVE DEMO</div>
        <div className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full bg-red-400" />
          <span className="h-2 w-2 rounded-full bg-yellow-400" />
          <span className="h-2 w-2 rounded-full brand-bg" />
        </div>
      </div>
      <div className="font-heading text-2xl tracking-tight mb-6">🍕 Dinner at Miss Margarita</div>

      {/* Amount */}
      <div className="rounded-xl border border-white/10 bg-black/40 p-5 mb-5">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-white/40 mb-2">Total amount</div>
        <div className="flex items-center">
          <span className="font-mono text-4xl text-white/50 mr-1">₹</span>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(Math.max(0, Number(e.target.value) || 0))}
            className="font-mono text-5xl font-semibold bg-transparent outline-none w-full"
            data-testid="demo-amount-input"
          />
        </div>
      </div>

      {/* People */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-white/40">Split between</div>
        <div className="flex items-center gap-2">
          <button
            onClick={remove}
            className="h-7 w-7 rounded-md border border-white/10 hover:border-white/25 flex items-center justify-center transition active:scale-95"
            data-testid="demo-remove-person"
            aria-label="Remove person"
          >
            <Minus className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
          <span className="font-mono text-sm w-6 text-center">{people.length}</span>
          <button
            onClick={add}
            className="h-7 w-7 rounded-md border border-white/10 hover:border-white/25 flex items-center justify-center transition active:scale-95"
            data-testid="demo-add-person"
            aria-label="Add person"
          >
            <Plus className="h-3.5 w-3.5" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <div className="space-y-2 mb-6">
        <AnimatePresence initial={false}>
          {people.map((p) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="flex items-center justify-between rounded-lg border border-white/5 bg-white/[0.02] px-3 py-2.5"
            >
              <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center text-sm">{p.emoji}</div>
                <span className="text-sm">{p.name}</span>
              </div>
              <span className="font-mono text-sm brand-text">₹{split}</span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <div className="rounded-xl border brand-border bg-[var(--brand)]/[0.08] p-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Check className="h-4 w-4 brand-text" strokeWidth={2} />
          <span className="text-sm">Each person owes</span>
        </div>
        <span className="font-mono text-lg font-semibold brand-text">₹{split}</span>
      </div>
    </div>
  );
};
